import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Plus, Save, Trash2, Lock, GripVertical, Loader2, AlertTriangle, EyeOff, Info,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ICON_CHOICES, resolveIcon } from "../lib/liveStatusIcons";
import { CATEGORY_CHOICES } from "../components/AddTileModal";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Team-Admin-Editor für das systemweite Live-Status-Schema (Feb 2026 ·
 * Struktur-Fix Live-Status).
 *
 * Der Admin kann:
 *   1. Neue Status-Punkte (Zeilen) hinzufügen
 *   2. Bestehende Punkte umbenennen (auch die 6 Basis-Punkte)
 *   3. Icon pro Punkt wählen
 *   4. Custom-Punkte löschen (Basis-Punkte sind LOCKED)
 *   5. Reihenfolge per Drag-and-Drop ändern (HTML5-DnD, kein Library)
 *
 * Speichern → PUT /api/team/live-status-schema — Änderungen wirken
 * sofort für alle Concierges und in der Kunden-App (nur bei
 * ausgewählten Kacheln).
 */

const NAVY = BRAND_COLORS.navy;
const GOLD = BRAND_COLORS.gold;
const RED = STATUS_COLORS.red;
const IVORY = BRAND_COLORS.ivory;

export default function TeamLiveStatusSchema() {
  const { effectiveRole } = useAuth();
  const [items, setItems] = useState([]);
  const [initial, setInitial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const isAdmin = effectiveRole === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/live-status-schema");
      setItems(data.items || []);
      setInitial(JSON.parse(JSON.stringify(data.items || [])));
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => JSON.stringify(items) !== JSON.stringify(initial), [items, initial]);

  const updateItem = (idx, patch) => {
    setItems((cur) => cur.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const addItem = () => {
    setItems((cur) => [...cur, {
      key: `custom_${Date.now().toString(36)}`,
      label: "Neuer Punkt",
      icon: "Circle",
      input_type: "text",
      locked: false,
      category: "sonstiges",
      placeholder: "",
      hint: "",
    }]);
  };

  const deleteItem = (idx) => {
    setItems((cur) => cur.filter((_, i) => i !== idx));
  };

  const moveItem = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    setItems((cur) => {
      const next = [...cur];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    });
  };

  const save = async () => {
    if (!isAdmin) {
      toast.error("Nur Admins dürfen das Schema speichern.");
      return;
    }
    setSaving(true);
    try {
      const payload = { items: items.map((it) => ({
        key: it.key,
        label: it.label,
        icon: it.icon || "Circle",
        input_type: it.input_type || "text",
        locked: !!it.locked,
        category: it.category || "sonstiges",
        placeholder: it.placeholder || null,
        hint: it.hint || null,
      }))};
      const { data } = await api.put("/team/live-status-schema", payload);
      setItems(data.items || []);
      setInitial(JSON.parse(JSON.stringify(data.items || [])));
      toast.success("Live-Status-Struktur gespeichert — Änderung ist sofort systemweit aktiv.");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="team-live-status-schema-page">
      <Link to="/team" className="inline-flex items-center gap-1 text-[11px] tracking-[0.15em] uppercase text-navy-dark/70 hover:text-gold">
        <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.6} /> Zum Team-Dashboard
      </Link>
      <div className="mt-6 mb-8">
        <div className="label-caps" style={{ color: GOLD }}>Systemweite Konfiguration</div>
        <h1 className="font-display text-3xl md:text-4xl text-navy mt-1 leading-tight">
          Live-Status · Struktur
        </h1>
        <p className="text-[13px] text-navy-dark/70 mt-3 leading-relaxed max-w-2xl">
          Master-Liste aller Punkte, die im Team-Editor „Objekte · Live-Status" erscheinen.
          Änderungen wirken sofort für alle Concierges. Die 6 Basis-Punkte
          (Schloss-Symbol) sind an den Inspection-Sync gebunden — sie können
          umbenannt und umgeordnet, aber NICHT gelöscht werden. Neue Punkte
          fügst du unten hinzu. Reihenfolge per Drag am Griff ändern.
        </p>
        {/* Feb 2026 · HARDCODED-Regel — Kunden-Dashboard zeigt nur die 6
            LOCKED-Kacheln. Custom-Punkte sind team-intern (Live-Status-
            Editor + Reports) und werden dem Kunden NIE als Kachel angezeigt. */}
        <div
          className="mt-4 p-3 md:p-4 flex items-start gap-2.5 text-[12.5px] leading-relaxed"
          style={{
            background: withAlpha(STATUS_COLORS.blue, 0.06),
            borderLeft: `3px solid ${STATUS_COLORS.blue}`,
            color: NAVY,
          }}
          data-testid="schema-customer-visibility-notice"
        >
          <Info className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.7} style={{ color: STATUS_COLORS.blue }} />
          <div>
            <div className="font-medium mb-0.5">Kunden-Dashboard zeigt nur die 6 Basis-Kacheln</div>
            <div className="text-navy-dark/75">
              Letzte Kontrolle · Temperatur · Wasser · Heizung · Alarm · Internet / WLAN erscheinen dem Kunden immer als 2×3-Grid. <span className="font-medium">Custom-Punkte sind team-intern</span> — sie fließen in Wochenberichte und in „Objekte · Live-Status", tauchen aber nicht auf dem Kunden-Dashboard auf.
            </div>
          </div>
        </div>
        {!isAdmin && (
          <div
            className="mt-4 p-3 flex items-start gap-2 text-[12px]"
            style={{ background: withAlpha(STATUS_COLORS.blue, 0.08), borderLeft: "3px solid #1F4B7A", color: NAVY }}
            data-testid="schema-readonly-hint"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.7} style={{ color: STATUS_COLORS.blue }} />
            <span>Read-only. Nur Admins dürfen das Live-Status-Schema bearbeiten.</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-navy-dark/60 text-[13px]">
          <Loader2 className="w-4 h-4 animate-spin" /> Schema wird geladen …
        </div>
      ) : (
        <>
          <ul className="space-y-2" data-testid="schema-list">
            {items.map((item, idx) => {
              const Icon = resolveIcon(item.icon);
              const locked = !!item.locked;
              return (
                <li
                  key={`${item.key}-${idx}`}
                  draggable={isAdmin && !locked ? true : (isAdmin ? true : false)}
                  onDragStart={() => setDragIndex(idx)}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex != null) moveItem(dragIndex, idx);
                    setDragIndex(null);
                  }}
                  onDragEnd={() => setDragIndex(null)}
                  className="bg-white border border-navy/10 p-3 md:p-4 flex items-start gap-3 transition-colors"
                  style={dragIndex === idx ? { borderColor: GOLD, background: withAlpha(BRAND_COLORS.gold, 0.05) } : {}}
                  data-testid={`schema-row-${item.key}`}
                >
                  <div className="flex flex-col items-center gap-1 pt-1 shrink-0 select-none">
                    <span
                      className="cursor-grab text-navy-dark/40 hover:text-navy"
                      title={isAdmin ? "Zum Umordnen ziehen" : "Read-only"}
                      data-testid={`schema-drag-${item.key}`}
                    >
                      <GripVertical className="w-4 h-4" strokeWidth={1.6} />
                    </span>
                    <span
                      className="w-9 h-9 rounded-md flex items-center justify-center"
                      style={{ background: IVORY }}
                    >
                      <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: NAVY }} />
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        value={item.label}
                        onChange={(e) => updateItem(idx, { label: e.target.value })}
                        disabled={!isAdmin}
                        placeholder="Bezeichnung"
                        data-testid={`schema-label-${item.key}`}
                        className="flex-1 min-w-[160px] bg-transparent border-b border-navy/15 focus:border-gold focus:outline-none px-1 py-1.5 text-[15px] text-navy font-display"
                      />
                      {locked ? (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5"
                          style={{ background: withAlpha(STATUS_COLORS.blue, 0.1), color: STATUS_COLORS.blue }}
                          title="Basis-Punkt — beim Kunden IMMER als Kachel sichtbar (Inspection-Sync gebunden)"
                          data-testid={`schema-badge-locked-${item.key}`}
                        >
                          <Lock className="w-3 h-3" strokeWidth={1.8} /> Basis · Kunde sieht Kachel
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5"
                          style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
                          title="Team-intern — dieser Punkt erscheint NICHT auf dem Kunden-Dashboard"
                          data-testid={`schema-badge-custom-${item.key}`}
                        >
                          <EyeOff className="w-3 h-3" strokeWidth={1.8} /> Team-intern · Kunde sieht nicht
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <label className="text-[11px] text-navy-dark/60 flex items-center gap-2">
                        Icon:
                        <select
                          value={item.icon || "Circle"}
                          onChange={(e) => updateItem(idx, { icon: e.target.value })}
                          disabled={!isAdmin}
                          data-testid={`schema-icon-${item.key}`}
                          className="bg-white border border-navy/15 focus:border-gold focus:outline-none px-2 py-1 text-[12px] text-navy"
                        >
                          {ICON_CHOICES.map((ic) => (
                            <option key={ic.value} value={ic.value}>{ic.label}</option>
                          ))}
                        </select>
                      </label>

                      <label className="text-[11px] text-navy-dark/60 flex items-center gap-2">
                        Kategorie:
                        <select
                          value={item.category || "sonstiges"}
                          onChange={(e) => updateItem(idx, { category: e.target.value })}
                          disabled={!isAdmin}
                          data-testid={`schema-category-${item.key}`}
                          className="bg-white border border-navy/15 focus:border-gold focus:outline-none px-2 py-1 text-[12px] text-navy"
                        >
                          {CATEGORY_CHOICES.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </label>

                      {!locked && (
                        <label className="text-[11px] text-navy-dark/60 flex items-center gap-2">
                          Eingabe:
                          <select
                            value={item.input_type || "text"}
                            onChange={(e) => updateItem(idx, { input_type: e.target.value })}
                            disabled={!isAdmin}
                            data-testid={`schema-input-type-${item.key}`}
                            className="bg-white border border-navy/15 focus:border-gold focus:outline-none px-2 py-1 text-[12px] text-navy"
                          >
                            <option value="text">Text</option>
                            <option value="number">Zahl</option>
                            <option value="date">Datum</option>
                          </select>
                        </label>
                      )}

                      <span className="text-[10.5px] text-navy-dark/40">
                        Schlüssel: <code className="font-mono">{item.key}</code>
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => deleteItem(idx)}
                    disabled={locked || !isAdmin}
                    title={locked ? "Basis-Punkt kann nicht gelöscht werden" : "Punkt löschen"}
                    data-testid={`schema-delete-${item.key}`}
                    className="shrink-0 p-2 border border-navy/15 hover:border-navy transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-white"
                    style={locked ? {} : { color: RED, borderColor: withAlpha(STATUS_COLORS.red, 0.25) }}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.6} />
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
            <button
              type="button"
              onClick={addItem}
              disabled={!isAdmin}
              data-testid="schema-add-item"
              className="inline-flex items-center gap-2 text-navy px-4 py-2 text-[11px] tracking-[0.15em] uppercase border border-navy/25 hover:border-gold hover:text-gold-dark bg-white transition-colors disabled:opacity-40"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.6} />
              Neuen Punkt hinzufügen
            </button>

            <div className="flex items-center gap-3">
              {dirty && (
                <span className="text-[11px] tracking-wider uppercase" style={{ color: GOLD }}>
                  Ungespeicherte Änderungen
                </span>
              )}
              <button
                type="button"
                onClick={save}
                disabled={!isAdmin || saving || !dirty}
                data-testid="schema-save"
                className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-40"
                style={{ background: NAVY }}
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" strokeWidth={1.6} />}
                Struktur speichern
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
